import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useHistory, useLocation } from 'react-router-dom';

import * as paths from 'app-router/paths';
import { useStore } from 'stores';
import { RequesterToken } from 'stores/RequesterTokenStore';

import Loading from 'components/Loading';
import Pagination from 'components/Pagination';
import { ErrorMessage, Message } from 'components/Message';
import { FindIcon, LabeledIcon, ReloadIcon } from 'components/Icon';

import { TabBar } from 'pages/Audience/TabBar';
import { RequesterTokenList } from './TableView';
import { SimpleInlineHelp } from 'components/InlineHelp';

export const Page: React.FC = observer(() =>
{
  const { uiStore, requesterTokenStore } = useStore();
  const history = useHistory();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const pageFromQuery = parseInt(query.get('p') || '1');
  const page = pageFromQuery ? pageFromQuery - 1 : 0;
  const searchQuery = query.get('q') || '';
  const [searchQueryCollector, setSearchQueryCollector] = useState(searchQuery);

  useEffect(() =>
  {
    requesterTokenStore.findAsync(searchQuery, page);
  }, [requesterTokenStore, searchQuery, page]);

  const reloadDataHandler = async () => await requesterTokenStore.findAsync(searchQuery, page);
  const searchHandler = () => history.push(paths.buildRequesterTokenListPath(searchQueryCollector, `${1}`));
  const onPageHandler = (page: number) => history.push(paths.buildRequesterTokenListPath(searchQueryCollector, `${page + 1}`));
  const createRequesterTokenHandler = () => history.push(paths.buildRequesterTokenCreatePath());
  const editRequesterTokenHandler = async (requester: RequesterToken) => history.push(paths.buildRequesterTokenEditPath(requester.token || ''));
  const deleteRequesterTokenHandler = async (requester: RequesterToken) =>
  {
    if (window.confirm(uiStore.labels.pages.audience.deleteRequesterTokenConfirmation.apply({requesterToken: requester.token})))
    {
      await requesterTokenStore.deleteAsync(requester.token || '');
      if (!requesterTokenStore.requesterToken.isError)
      {
        await reloadDataHandler();
      }
    }
  };
  return (
    <article className="page audience requester-token-list">
      <h1>{uiStore.labels.pages.audience.title}<SimpleInlineHelp>{uiStore.labels.help.audience.about}</SimpleInlineHelp></h1>
      <TabBar />
      {
        requesterTokenStore.pagedRequesterTokens.isLoading
        ? <Loading />
        : requesterTokenStore.pagedRequesterTokens.isError
          ? <ErrorMessage>
              {requesterTokenStore.pagedRequesterTokens.errorMessage}
              <button onClick={reloadDataHandler}>
                <LabeledIcon label={uiStore.labels.common.tryAgain}><ReloadIcon/></LabeledIcon>
              </button>
            </ErrorMessage>
          : <div className="content">
              <div className="action-bar">
                <button onClick={reloadDataHandler}><ReloadIcon/></button>
                <button onClick={createRequesterTokenHandler}>{uiStore.labels.pages.audience.createNewRequesterToken}</button>
                <input type="text"
                       className="search"
                       placeholder={uiStore.labels.pages.audience.searchForRequesterTokens}
                       value={searchQueryCollector}
                       onChange={e => setSearchQueryCollector(prev => e.target.value)}
                       onKeyPress={async e => e.key === 'Enter' ? searchHandler() : null}/>
                <button onClick={searchHandler}><FindIcon/></button>
              </div>
              <div className="pagination">
                <Pagination currentPage={requesterTokenStore.pagedRequesterTokens.data?.page || 0}
                            totalPages={requesterTokenStore.pagedRequesterTokens.data?.total || 0}
                            onPage={onPageHandler}/>
              </div>
              {
                requesterTokenStore.pagedRequesterTokens.data?.data && requesterTokenStore.pagedRequesterTokens.data.data.length > 0
                ? <RequesterTokenList requesterTokens={requesterTokenStore.pagedRequesterTokens.data} deleteRequesterTokenHandler={deleteRequesterTokenHandler} editRequesterTokenHandler={editRequesterTokenHandler} />
                : <Message>{uiStore.labels.common.noResults}</Message>
              }
            </div>
      }
    </article>
  );
});